// src/components/SmartFlow/shared/KeyValueEditor.jsx

import React, { useState, useEffect, useRef } from "react";

/**
 * Key-Value Pair Editor Component
 *
 * Shared component used for editing headers, query parameters, form data, etc.
 * Provides a user-friendly interface for adding/editing/removing key-value pairs.
 *
 * Props:
 * - value: Object with key-value pairs (e.g., { "Content-Type": "application/json" })
 * - onChange: Function called when value changes
 * - placeholder: Object with placeholder examples (optional)
 * - allowExpressions: Boolean to show expression hints (default: true)
 */
const KeyValueEditor = ({ value = {}, onChange, placeholder = {}, allowExpressions = true }) => {
  // Convert object to array of {key, value} for easier editing
  const [pairs, setPairs] = useState([]);

  // Ref para rastrear se o componente está montado
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    // Convert value object to array
    const pairsArray = Object.entries(value || {}).map(([key, val]) => ({
      key,
      value: val,
      id: Math.random().toString(36).substr(2, 9),
    }));

    // Add empty row if no pairs exist
    if (pairsArray.length === 0) {
      pairsArray.push({ key: "", value: "", id: Math.random().toString(36).substr(2, 9) });
    }

    if (isMountedRef.current) {
      setPairs(pairsArray);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [JSON.stringify(value)]);

  const handlePairChange = (id, field, newValue) => {
    if (!isMountedRef.current) return;

    const updatedPairs = pairs.map((pair) => (pair.id === id ? { ...pair, [field]: newValue } : pair));

    setPairs(updatedPairs);

    // Convert back to object and notify parent
    const obj = {};
    updatedPairs.forEach((pair) => {
      if (pair.key.trim() !== "") {
        obj[pair.key.trim()] = pair.value;
      }
    });

    if (onChange) {
      onChange(obj);
    }
  };

  const addPair = () => {
    if (!isMountedRef.current) return;

    setPairs([...pairs, { key: "", value: "", id: Math.random().toString(36).substr(2, 9) }]);
  };

  const removePair = (id) => {
    if (!isMountedRef.current) return;

    const updatedPairs = pairs.filter((pair) => pair.id !== id);

    // Always keep at least one empty row
    if (updatedPairs.length === 0) {
      updatedPairs.push({ key: "", value: "", id: Math.random().toString(36).substr(2, 9) });
    }

    setPairs(updatedPairs);

    // Convert back to object and notify parent
    const obj = {};
    updatedPairs.forEach((pair) => {
      if (pair.key.trim() !== "") {
        obj[pair.key.trim()] = pair.value;
      }
    });

    if (onChange) {
      onChange(obj);
    }
  };

  const styles = {
    container: {
      border: "1px solid #ddd",
      borderRadius: "4px",
      padding: "8px",
      background: "#fafafa",
    },
    row: {
      display: "flex",
      gap: "8px",
      marginBottom: "8px",
      alignItems: "center",
    },
    input: {
      flex: 1,
      padding: "6px 8px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      fontSize: "13px",
      fontFamily: "inherit",
    },
    keyInput: {
      flex: "0 0 35%",
      fontFamily: "monospace",
    },
    valueInput: {
      flex: "1",
      fontFamily: "monospace",
    },
    removeButton: {
      padding: "6px 10px",
      background: "#e74c3c",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "bold",
    },
    addButton: {
      padding: "8px 16px",
      background: "#3498db",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "bold",
      width: "100%",
      marginTop: "4px",
    },
    header: {
      display: "flex",
      gap: "8px",
      marginBottom: "8px",
      fontSize: "11px",
      fontWeight: "bold",
      color: "#7f8c8d",
      paddingLeft: "2px",
    },
    hint: {
      fontSize: "11px",
      color: "#7f8c8d",
      marginTop: "8px",
      fontStyle: "italic",
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={{ flex: "0 0 35%" }}>Key</div>
        <div style={{ flex: "1" }}>Value</div>
        <div style={{ width: "60px" }}></div>
      </div>

      {pairs.map((pair, index) => (
        <div key={pair.id} style={styles.row}>
          <input
            type="text"
            value={pair.key}
            onChange={(e) => handlePairChange(pair.id, "key", e.target.value)}
            placeholder={Object.keys(placeholder)[index] || "Key"}
            style={{ ...styles.input, ...styles.keyInput }}
          />
          <input
            type="text"
            value={pair.value}
            onChange={(e) => handlePairChange(pair.id, "value", e.target.value)}
            placeholder={Object.values(placeholder)[index] || (allowExpressions ? "Value or {{ expression }}" : "Value")}
            style={{ ...styles.input, ...styles.valueInput }}
          />
          <button
            onClick={() => removePair(pair.id)}
            style={styles.removeButton}
            disabled={pairs.length === 1 && pair.key === "" && pair.value === ""}
          >
            ✕
          </button>
        </div>
      ))}

      <button onClick={addPair} style={styles.addButton}>
        + Add Pair
      </button>

      {allowExpressions && (
        <div style={styles.hint}>💡 You can use expressions like {`{{ trigger.value }}`} or {`{{ variables.token }}`}</div>
      )}
    </div>
  );
};

export default KeyValueEditor;
